import {
  Directive,
  LOCALE_ID,
  Signal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { InsightsService } from '@maximizer/core/shared/insights';
import { CalendarService } from '@maximizer/outlook/main/data-access';
import {
  AppointmentOccurrenceLimitError,
  AppointmentSeriesError,
  CalendarStore,
  OutlookStore,
  describeSaveAppointmentResult,
  recurrenceText,
  seriesProgressParams,
} from '@maximizer/outlook/shared/data-access';
import {
  AppointmentPrefill,
  AttendeeAddEntryEvent,
  CalendarAttendee,
  SaveAppointmentResult,
  SeriesSaveProgress,
} from '@maximizer/outlook/shared/domain';
import {
  AttendeeNotifyEvent,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslateService } from '@ngx-translate/core';

export type SaveAppointmentDisabledReason =
  | 'syncOn'
  | 'checking'
  | 'saving'
  | 'alreadySaved'
  | 'eventNotSavedInOutlook'
  | 'none';

const SAVE_APPOINTMENT_TOOLTIP_KEY: Record<
  SaveAppointmentDisabledReason,
  string
> = {
  syncOn: 'outlook.calendar.saveAppointment.syncOnTooltip',
  checking: 'outlook.calendar.saveAppointment.tooltip',
  saving: 'outlook.calendar.saveAppointment.tooltip',
  alreadySaved: 'outlook.calendar.saveAppointment.saved',
  eventNotSavedInOutlook: 'outlook.calendar.saveAppointment.unavailable',
  none: 'outlook.calendar.saveAppointment.tooltip',
};

const SAVE_APPOINTMENT_TOOLTIP_WIDTH: Record<
  SaveAppointmentDisabledReason,
  number
> = {
  syncOn: 300,
  checking: 200,
  saving: 200,
  alreadySaved: 225,
  eventNotSavedInOutlook: 250,
  none: 200,
};

const SAVE_APPOINTMENT_ICON = 'maximizer-icons icon-16 icon-add-appointment';
const SAVED_APPOINTMENT_ICON = 'maximizer-icons icon-16 icon-saved-appointment';
const SYNC_ON_ICON = 'fa-icons icon-regular icon-14 icon-arrows-rotate';
const BUSY_ICON = 'fa-icons icon-solid icon-14 icon-circle-notch animate-spin';

/**
 * Save-appointment behaviour shared by the calendar read and compose pages:
 * button state, auto-save or review form, result and error messaging.
 * The page owns the template, the notification query and any extra guard.
 */
@Directive()
export abstract class OutlookCalendarSaveAppointmentDirective {
  abstract readonly notification: Signal<OutlookNotificationComponent>;

  readonly savingAppointment = signal(false);
  readonly resolvingSavedAppointment = signal(false);
  readonly savedLookupFailed = signal(false);
  readonly saveProgress = signal<SeriesSaveProgress | null>(null);
  readonly appointmentPrefill = signal<AppointmentPrefill | null>(null);

  readonly saveAppointmentDisabledReason =
    computed<SaveAppointmentDisabledReason>(() => {
      if (this.outlookStore.appointmentSyncEnabled()) return 'syncOn';
      if (this.resolvingSavedAppointment()) return 'checking';
      if (this.savingAppointment()) return 'saving';
      if (this.calendarStore.isAppointmentSaved()) return 'alreadySaved';
      return this.pageDisabledReason() ?? 'none';
    });

  readonly saveAppointmentTooltipKey = computed(
    () => SAVE_APPOINTMENT_TOOLTIP_KEY[this.saveAppointmentDisabledReason()],
  );

  readonly saveAppointmentTooltipWidth = computed(
    () => SAVE_APPOINTMENT_TOOLTIP_WIDTH[this.saveAppointmentDisabledReason()],
  );

  readonly saveAppointmentLabelKey = computed(() => {
    if (this.saveAppointmentDisabledReason() === 'syncOn') {
      return 'outlook.calendar.saveAppointment.syncOn';
    }
    if (this.savingAppointment() && this.saveProgress()) {
      return 'outlook.appointment.recurrence.saving';
    }
    return 'outlook.calendar.saveAppointment.button';
  });

  readonly saveAppointmentLabelParams = computed(() => {
    const progress = this.saveProgress();
    return progress ? seriesProgressParams(progress) : {};
  });

  readonly saveAppointmentIconClass = computed(() => {
    switch (this.saveAppointmentDisabledReason()) {
      case 'syncOn':
        return SYNC_ON_ICON;
      case 'checking':
      case 'saving':
        return BUSY_ICON;
      case 'alreadySaved':
        return SAVED_APPOINTMENT_ICON;
      default:
        return SAVE_APPOINTMENT_ICON;
    }
  });

  protected constructor(
    protected readonly calendarService: CalendarService,
    protected readonly translate: TranslateService,
    protected readonly insightsService: InsightsService,
    public readonly calendarStore: CalendarStore,
    public readonly outlookStore: OutlookStore,
  ) {}

  /** A page-specific reason that applies once the shared guards pass. */
  protected pageDisabledReason(): SaveAppointmentDisabledReason | null {
    return null;
  }

  /** Whether the saved-appointment lookup can run for this item. */
  protected isEventSavedInOutlook(): Promise<boolean> {
    return Promise.resolve(true);
  }

  async onSaveAppointment(): Promise<void> {
    if (this.saveAppointmentDisabledReason() !== 'none') return;
    if (
      this.savedLookupFailed() &&
      !(await this.retrySavedAppointmentLookup())
    ) {
      return;
    }

    if (this.outlookStore.preferences().saveAppointmentDetail) {
      await this.openAppointmentForm();
      return;
    }

    await this.saveAppointment();
  }

  onAppointmentFormSaved(result: SaveAppointmentResult): void {
    this.appointmentPrefill.set(null);
    this.calendarService.trackAppointmentFormSaved(result);
    this.showSaveResult(result);
  }

  onAppointmentFormPartiallySaved(error: AppointmentSeriesError): void {
    this.appointmentPrefill.set(null);
    this.showSaveError(error);
  }

  onAppointmentFormSavingChange(saving: boolean): void {
    this.savingAppointment.set(saving);
  }

  // Closing the dialog mid-save would orphan the request: rows get created, the key is never recorded.
  onAppointmentFormCancelled(): void {
    if (this.savingAppointment()) return;
    this.appointmentPrefill.set(null);
  }

  onNavigate(attendee: CalendarAttendee): void {
    this.calendarService.navigateToAttendee(attendee);
  }

  onNotify(event: AttendeeNotifyEvent): void {
    this.notification().showMessage(event.type, event.message, true);
  }

  onAddEntry(event: AttendeeAddEntryEvent): void {
    this.calendarService.navigateToAddEntry(event.event.route, event.attendee);
  }

  // The button stays disabled until the lookup settles, and a failed lookup is re-run on click,
  // so neither a fast click nor a transient error can duplicate an existing appointment.
  protected async loadSavedAppointment(): Promise<void> {
    this.calendarStore.setSavedAppointmentKey(null);
    this.resolvingSavedAppointment.set(true);
    try {
      this.savedLookupFailed.set(false);
      if (!(await this.isEventSavedInOutlook())) return;

      this.calendarStore.setSavedAppointmentKey(
        await this.calendarService.resolveSavedAppointmentKey(),
      );
    } catch (error) {
      this.savedLookupFailed.set(true);
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.resolvingSavedAppointment.set(false);
    }
  }

  private async retrySavedAppointmentLookup(): Promise<boolean> {
    await this.loadSavedAppointment();
    if (this.savedLookupFailed()) {
      this.notification().showMessage(
        'error',
        'outlook.calendar.saveAppointment.lookupFailed',
        true,
      );
      return false;
    }
    return this.saveAppointmentDisabledReason() === 'none';
  }

  private async saveAppointment(): Promise<void> {
    this.savingAppointment.set(true);

    try {
      this.showSaveResult(
        await this.calendarService.saveAppointment(
          this.calendarStore.attendees(),
          (progress) => this.saveProgress.set(progress),
        ),
      );
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
      await this.showSaveFailure(error);
    } finally {
      this.savingAppointment.set(false);
      this.saveProgress.set(null);
    }
  }

  // A timed-out create is never replayed but may have committed, so the lookup decides
  // whether the user sees "saved" or the error.
  private async showSaveFailure(error: unknown): Promise<void> {
    if (
      error instanceof AppointmentSeriesError ||
      error instanceof AppointmentOccurrenceLimitError
    ) {
      this.showSaveError(error);
      return;
    }

    const savedKey = await this.findSavedAppointmentKey();
    if (savedKey) {
      this.showSaveResult({
        appointmentKey: savedKey,
        hasUnsupportedRecurrence: false,
        savedThrough: null,
      });
      return;
    }
    this.showSaveError(error);
  }

  private async findSavedAppointmentKey(): Promise<string | null> {
    try {
      return await this.calendarService.resolveSavedAppointmentKey();
    } catch {
      // The save error is the one the user needs to see; a failed reconciliation adds nothing.
      return null;
    }
  }

  private async openAppointmentForm(): Promise<void> {
    this.savingAppointment.set(true);
    try {
      this.appointmentPrefill.set(
        await this.calendarService.buildAppointmentPrefill(
          this.calendarStore.attendees(),
        ),
      );
    } catch (error) {
      this.notification().showMessage(
        'error',
        'outlook.calendar.saveAppointment.error',
        true,
      );
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.savingAppointment.set(false);
    }
  }

  private showSaveResult(result: SaveAppointmentResult): void {
    this.calendarStore.setSavedAppointmentKey(result.appointmentKey);
    this.notification().showMessage(
      'success',
      describeSaveAppointmentResult(result, this.recurrenceText),
    );
  }

  private showSaveError(error: unknown): void {
    if (error instanceof AppointmentSeriesError) {
      this.calendarStore.setSavedAppointmentKey(error.appointmentKey);
      this.notification().showMessage(
        'error',
        this.translate.instant(
          'outlook.appointment.recurrence.partialError',
          seriesProgressParams(error.progress),
        ),
      );
      return;
    }
    if (error instanceof AppointmentOccurrenceLimitError) {
      this.notification().showMessage(
        'error',
        this.translate.instant(
          'outlook.calendar.saveAppointment.errorTooManyOccurrences',
          { max: error.maxOccurrences },
        ),
      );
      return;
    }
    this.notification().showMessage(
      'error',
      'outlook.calendar.saveAppointment.error',
      true,
    );
  }

  private readonly localeId = inject(LOCALE_ID);
  private readonly recurrenceText = recurrenceText(
    (key, params) => this.translate.instant(key, params),
    this.localeId,
  );
}
